import { useEffect, useState } from 'react';
import { CalendarClock, Check, X, ExternalLink, RefreshCw, Pencil, Plus } from 'lucide-react';
import { api } from '../api';
import { useToast } from './ui';

/**
 * Predvedenie stroja pri cenovej ponuke (.669).
 *
 * Termín, miesto a stroj. Po uložení sa zapíše do Google kalendára (keď je
 * pripojený v Nastaveniach → Google kalendár); zmena termínu sa prenesie,
 * zrušenie udalosť z kalendára odstráni.
 */
const DNI = ['ne', 'po', 'ut', 'st', 'št', 'pi', 'so'];
export const casSk = (v) => {
  if (!v) return '';
  const [d, t] = String(v).split('T');
  const [y, m, dd] = d.split('-').map(Number);
  const den = DNI[new Date(y, m - 1, dd).getDay()];
  return `${den} ${dd}. ${m}. ${y}${t ? ` ${t.slice(0, 5)}` : ''}`;
};
const STAV = {
  planovane: { text: 'Plánované', trieda: 'bg-indigo-100 text-indigo-800' },
  hotove: { text: 'Hotové', trieda: 'bg-emerald-100 text-emerald-800' },
  zrusene: { text: 'Zrušené', trieda: 'bg-slate-200 text-slate-600 line-through' },
};

const prazdny = (customer, items) => {
  const z = new Date(Date.now() + 86400e3);
  const d = `${z.getFullYear()}-${String(z.getMonth() + 1).padStart(2, '0')}-${String(z.getDate()).padStart(2, '0')}`;
  const adresa = [customer?.street, [customer?.zip, customer?.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const stroj = (items || []).find((i) => i.product_id && Number(i.unit_price) > 0)?.name || '';
  return { datum: d, od: '09:00', do: '10:00', miesto: adresa, stroj, poznamka: '' };
};

export default function PredvedenieStroja({ quoteId, customer, items }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);       // null | { id?, datum, od, do, miesto, stroj, poznamka }
  const [busy, setBusy] = useState(false);

  const nacitaj = () => api.get(`/api/predvedenia?quote_id=${quoteId}`).then(setData).catch(() => setData({ zoznam: [] }));
  useEffect(() => { if (quoteId) nacitaj(); }, [quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const uloz = async () => {
    setBusy(true);
    try {
      const telo = {
        quote_id: quoteId, zaciatok: `${form.datum}T${form.od}`, koniec: form.do ? `${form.datum}T${form.do}` : null,
        miesto: form.miesto, stroj: form.stroj, poznamka: form.poznamka,
      };
      const v = form.id ? await api.put(`/api/predvedenia/${form.id}`, telo) : await api.post('/api/predvedenia', telo);
      if (v.google?.chyba) toast.error(`Uložené v CRM, ale Google kalendár: ${v.google.chyba}`);
      else toast.success(v.google?.event_id ? 'Predvedenie uložené aj v Google kalendári.' : 'Predvedenie uložené.');
      setForm(null); nacitaj();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const stav = async (p, s) => {
    let vysledok = null;
    if (s === 'hotove') vysledok = window.prompt('Ako dopadlo predvedenie? (nepovinné)') || null;
    if (s === 'zrusene' && !window.confirm('Zrušiť predvedenie? Z Google kalendára sa odstráni.')) return;
    try { await api.post(`/api/predvedenia/${p.id}/stav`, { stav: s, vysledok }); nacitaj(); }
    catch (e) { toast.error(e.message); }
  };
  const google = async (p) => {
    try {
      const v = await api.post(`/api/predvedenia/${p.id}/google`, {});
      if (v.google?.chyba) toast.error(v.google.chyba); else toast.success('Zapísané do Google kalendára.');
      nacitaj();
    } catch (e) { toast.error(e.message); }
  };

  const zoznam = data?.zoznam || [];
  return (
    <div className="card-p space-y-2">
      <div className="flex items-center gap-2">
        <CalendarClock size={18} className="text-indigo-600" />
        <h2 className="font-semibold text-slate-700">Predvedenie stroja</h2>
        {!form && (
          <button className="btn-secondary ml-auto !px-2 !py-1 text-xs" onClick={() => setForm(prazdny(customer, items))}>
            <Plus size={13} /> Dohodnúť
          </button>
        )}
      </div>
      {data && !data.google?.pripojeny && (
        <p className="text-[11px] text-slate-400">Google kalendár nie je pripojený – administrátor ho pripojí v Nastaveniach → Google kalendár.</p>
      )}

      {zoznam.map((p) => (
        <div key={p.id} className={`rounded-lg p-2.5 text-sm ring-1 ${p.stav === 'planovane' ? 'bg-indigo-50 ring-indigo-200' : 'bg-slate-50 ring-slate-200'}`}>
          <div className="flex flex-wrap items-center gap-2">
            <b className="text-indigo-900">{casSk(p.zaciatok)}{p.koniec ? `–${String(p.koniec).slice(11, 16)}` : ''}</b>
            <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${STAV[p.stav]?.trieda || ''}`}>{STAV[p.stav]?.text || p.stav}</span>
          </div>
          {p.stroj && <div className="text-slate-700">{p.stroj}</div>}
          {p.miesto && <div className="text-xs text-slate-500">{p.miesto}</div>}
          {p.poznamka && <div className="mt-0.5 whitespace-pre-wrap text-xs text-slate-600">{p.poznamka}</div>}
          {p.vysledok && <div className="mt-0.5 text-xs text-emerald-800">Výsledok: {p.vysledok}</div>}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            {p.google_link
              ? <a className="inline-flex items-center gap-1 text-indigo-700 hover:underline" href={p.google_link} target="_blank" rel="noreferrer">v Google kalendári <ExternalLink size={11} /></a>
              : p.stav !== 'zrusene' && data?.google?.pripojeny && <button className="inline-flex items-center gap-1 text-indigo-700 underline" onClick={() => google(p)}><RefreshCw size={11} /> zapísať do Google</button>}
            {p.google_chyba && p.stav !== 'zrusene' && <span className="text-red-600" title={p.google_chyba}>Google: {p.google_chyba}</span>}
            {p.stav === 'planovane' && (
              <span className="ml-auto flex gap-2">
                <button className="inline-flex items-center gap-0.5 text-slate-600 hover:underline" onClick={() => setForm({
                  id: p.id, datum: p.zaciatok.slice(0, 10), od: p.zaciatok.slice(11, 16), do: p.koniec ? p.koniec.slice(11, 16) : '',
                  miesto: p.miesto || '', stroj: p.stroj || '', poznamka: p.poznamka || '',
                })}><Pencil size={11} /> zmeniť</button>
                <button className="inline-flex items-center gap-0.5 text-emerald-700 hover:underline" onClick={() => stav(p, 'hotove')}><Check size={12} /> hotové</button>
                <button className="inline-flex items-center gap-0.5 text-red-600 hover:underline" onClick={() => stav(p, 'zrusene')}><X size={12} /> zrušiť</button>
              </span>
            )}
          </div>
        </div>
      ))}

      {form && (
        <div className="space-y-2 rounded-lg bg-white p-2.5 ring-1 ring-indigo-300">
          <div className="grid grid-cols-3 gap-2">
            <label className="col-span-3 text-xs sm:col-span-1">Dátum<input type="date" className="input !py-1" value={form.datum} onChange={set('datum')} /></label>
            <label className="text-xs">Od<input type="time" className="input !py-1" value={form.od} onChange={set('od')} /></label>
            <label className="text-xs">Do<input type="time" className="input !py-1" value={form.do} onChange={set('do')} /></label>
          </div>
          <label className="block text-xs">Stroj
            <input className="input !py-1" list={`predv-stroje-${quoteId}`} value={form.stroj} onChange={set('stroj')} placeholder="napr. Kärcher B 50 W" />
            <datalist id={`predv-stroje-${quoteId}`}>
              {(items || []).filter((i) => i.name).map((i, k) => <option key={k} value={i.name} />)}
            </datalist>
          </label>
          <label className="block text-xs">Miesto<input className="input !py-1" value={form.miesto} onChange={set('miesto')} placeholder="adresa predvedenia" /></label>
          <label className="block text-xs">Poznámka<textarea className="input min-h-[50px] !py-1" value={form.poznamka} onChange={set('poznamka')} placeholder="kto príde, čo pripraviť…" /></label>
          <div className="flex gap-2">
            <button className="btn-primary !py-1.5 text-sm" disabled={busy || !form.datum || !form.od} onClick={uloz}>
              <Check size={15} /> {busy ? 'Ukladám…' : form.id ? 'Uložiť zmenu' : 'Uložiť predvedenie'}
            </button>
            <button className="btn-ghost !py-1.5 text-sm" onClick={() => setForm(null)}>Zrušiť</button>
          </div>
        </div>
      )}
      {!zoznam.length && !form && <p className="text-xs text-slate-400">Zatiaľ nie je dohodnuté žiadne predvedenie.</p>}
    </div>
  );
}
